import { useCallback } from "react";
import { useIntl } from "react-intl";
import { toast } from "react-toastify";
import { DEMO_REQUEST_LOADING } from "../../constants/loading.constant";
import { useGlobalCore } from "../globals";
import { demoApiService } from "./services/api";
import {
  CreateDemoRequestBody,
  DemoStatus,
} from "./interfaces/DemoRequest";

export function useDemoCore() {
  const intl = useIntl();
  const { turnOnLoading, turnOffLoading } = useGlobalCore();

  const createDemoRequest = useCallback(
    async (body: CreateDemoRequestBody, onSuccess?: () => void) => {
      try {
        turnOnLoading(DEMO_REQUEST_LOADING);
        const demoRequest = await demoApiService.createDemoRequest(body);
        toast.success(intl.formatMessage({ id: "app.demo.toast.success" }));
        turnOffLoading(DEMO_REQUEST_LOADING, 500);
        onSuccess?.();
        return demoRequest;
      } catch (error: any) {
        toast.error(intl.formatMessage({ id: "app.demo.toast.error" }));
        turnOffLoading(DEMO_REQUEST_LOADING);
        console.error(error);
        return null;
      }
    },
    [intl, turnOnLoading, turnOffLoading]
  );

  const getDemoRequests = useCallback(async (status?: DemoStatus) => {
    try {
      return await demoApiService.getDemoRequests(status);
    } catch (error: any) {
      console.error(error);
      return [];
    }
  }, []);

  const updateDemoRequestStatus = useCallback(
    async (id: number, status: DemoStatus) => {
      try {
        const updated = await demoApiService.updateDemoRequestStatus(
          id,
          status
        );
        toast.success(intl.formatMessage({ id: "app.demo.toast.statusUpdated" }));
        return updated;
      } catch (error: any) {
        toast.error(intl.formatMessage({ id: "app.demo.toast.error" }));
        console.error(error);
        return null;
      }
    },
    [intl]
  );

  return {
    createDemoRequest,
    getDemoRequests,
    updateDemoRequestStatus,
  };
}
